// ResourceDetail — the Bibliotek's ITEM view: the library row the author has
// gone into, at the whole centre (library drill-in). A projection: the item's
// content arrives as the library module's `Remote` read (modules/library),
// never loaded here.
//
// By kind (owner "START SIMPLE", views > source):
//   - source   → the `DetailWorkbench` (F84).
//   - package  → the REAL `ExportView`.
//   - material → the host's `renderMaterial` slot: the library edits a
//     material IN PLACE through the editor host (library layout 2026-10-01),
//     which this leaf cannot reach. `MaterialDetail` below stays the
//     read-only face (stories); the right panel reads a material through
//     rightPanel/MaterialFocus.
//   - file     → a metadata card (media type, added, description). No preview:
//     a File's bytes aren't stored yet. "Make it a source" opens an inline
//     form for the author's text about the file; the host runs the promotion
//     (`onPromoteFile`) and selects the new source.
//   - conversation → a light identity card (the Library scopes conversations
//     out; they live in the chat rail).
//
// Every detail owns its whole header (title, quiet facts, primary action);
// there is no "Open" for any kind (library layout 2026-10-01).
//
// Every read renders explicit states (A2): a skeleton while in flight, a
// kind-aware "couldn't load" with a Retry intent, or the loaded view (kept
// while it is read again). The right panel's focus views read their own
// content through `usePortLoad` into the same `Remote` and render the same
// faces. The pure half (copy, file facts, the promote intent) lives in
// `resourceDetailModel.ts`.

import { Remote, Result } from "@bilda/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect, useState } from "react";
import { Button } from "../../components/Button/Button.js";
import { DetailWorkbench } from "../../components/DetailWorkbench/DetailWorkbench.js";
import { EmptyState } from "../../components/EmptyState/EmptyState.js";
import type { ExportViewProps } from "../../components/ExportView/ExportView.js";
import { ExportView } from "../../components/ExportView/ExportView.js";
import { Field } from "../../components/Field/Field.js";
import { Heading } from "../../components/Heading/Heading.js";
import { Icon } from "../../components/Icon/Icon.js";
import { MaterialPreview } from "../../components/MaterialPreview/MaterialPreview.js";
import type {
    PageSpan,
    ResourceRef,
} from "../../components/Navigation/types.js";
import type { MaterialQuality } from "../../components/QualitySummary/QualitySummary.js";
import type {
    SearchResult,
    SearchResultKind,
} from "../../components/Search/searchModel.js";
import { Skeleton } from "../../components/Skeleton/Skeleton.js";
import { Text } from "../../components/Text/Text.js";
import { Textarea } from "../../components/Textarea/Textarea.js";
import type { ItemContent } from "../../modules/library.js";
import type { ExternalError } from "../externalError.js";
import type { LoadedExport } from "../ports/packaging.js";
import type {
    LoadedFile,
    LoadedMaterial,
    LoadedSource,
} from "../ports/resource.js";
import type { PortResult } from "../ports/result.js";
import { useRowVersion } from "./liveLibrary.js";
import {
    type FileFact,
    fileFacts,
    type PromoteFileOutcome,
    type PromoteFileToSourceIntent,
    promoteIntentOf,
    unavailableCopy,
} from "./resourceDetailModel.js";
import "./ResourceDetail.css";

type Load<T> = Remote.Remote<T, ExternalError>;

export type ResourceDetailProps = {
    // The row the author went into. Its `kind` picks the view; `title` rides
    // the view header (a loaded `SourceMeta` carries the filename, not the
    // author-facing title).
    readonly result: SearchResult;
    // The item's content read (source / package / file), and read it again.
    readonly load: Load<ItemContent>;
    readonly onRetry: () => void;
    // A source's PDF page span to open at and mark (a chat tool's read locator).
    readonly focusPages?: PageSpan;
    // The opened material — the host's in-place editor.
    readonly renderMaterial: (material: SearchResult) => ReactNode;
    // The finished-package download DOM trigger (impure, no result) — wired to
    // ExportView's ready-state action, the same seam the resource tab uses.
    readonly downloadExport: (url: string) => void;
    // Following a lineage link (a source's derived material, a material's
    // "Created from" source) — the host's `openRef`.
    readonly onOpenRef: (ref: ResourceRef) => void;
    // F88 — "Create material from this source": the source's primary CTA.
    readonly onDraftFromSource: (source: ResourceRef) => void;
    // A source whose stored content is missing: re-upload its original file
    // INTO it; `onRestored` reads this item again once it is back.
    readonly onReuploadSource?: (
        source: ResourceRef,
        onRestored: () => void,
    ) => void;
    // "Make it a source" on a file: the host runs the promotion and, on
    // success, opens the new source; the outcome settles the form.
    readonly onPromoteFile: (
        intent: PromoteFileToSourceIntent,
    ) => Promise<PromoteFileOutcome>;
};

export const ResourceDetail = (props: ResourceDetailProps): ReactNode => {
    const { result } = props;
    switch (result.kind) {
        case "material":
            return props.renderMaterial(result);
        case "conversation":
            return (
                <div className="bilda-resource-detail__centered">
                    <EmptyState
                        icon={<Icon name="chat" size="lg" />}
                        title={result.title}
                        body={
                            <Trans>Open the conversation to continue it.</Trans>
                        }
                    />
                </div>
            );
        case "source":
        case "package":
        case "file":
            return (
                <LoadView
                    load={props.load}
                    kind={result.kind}
                    onRetry={props.onRetry}
                    render={(content) => itemView(props, content)}
                />
            );
    }
};

const itemView = (
    props: ResourceDetailProps,
    content: ItemContent,
): ReactNode => {
    const { result } = props;
    switch (content._tag) {
        case "Item.Source":
            return (
                <SourceView
                    result={result}
                    source={content.source}
                    focusPages={props.focusPages}
                    onOpenRef={props.onOpenRef}
                    onDraftFromSource={props.onDraftFromSource}
                    onReuploadSource={props.onReuploadSource}
                    onRestored={props.onRetry}
                />
            );
        case "Item.Package":
            return (
                <PackageView
                    title={result.title}
                    pkg={content.pkg}
                    downloadExport={props.downloadExport}
                    onOpenRef={props.onOpenRef}
                />
            );
        case "Item.File":
            return (
                <FileView
                    id={result.id}
                    title={result.title}
                    file={content.file}
                    onPromoteFile={props.onPromoteFile}
                />
            );
    }
};

// One read's explicit states: the skeleton while cold, the kind-aware
// "couldn't load" with Retry, the value (also while it is read again).
export const LoadView = <T,>({
    load,
    kind,
    onRetry,
    render,
}: {
    readonly load: Load<T>;
    readonly kind: SearchResultKind;
    readonly onRetry: () => void;
    readonly render: (value: T) => ReactNode;
}): ReactNode =>
    Remote.match<T, ExternalError, ReactNode>({
        notAsked: () => <DetailSkeleton />,
        loading: () => <DetailSkeleton />,
        reloading: render,
        loaded: render,
        failed: () => <UnavailableDetail kind={kind} onRetry={onRetry} />,
    })(load);

// One port read keyed by `id`, cancellation-guarded (the last id wins), for
// the right panel's focus views. `retry` re-runs the read; the attempt
// counter is view-ephemeral. The read follows the live library: when this
// resource's row moves (`useRowVersion`: written by the assistant, a job,
// another tab) it reads again, keeping the shown value while it does, and a
// failed re-read keeps it too (the view never blanks on a blip).
export const usePortLoad = <T,>(
    read: (id: string) => PortResult<T>,
    id: string,
): { readonly load: Load<T>; readonly retry: () => void } => {
    // The value is held with the id it was read for: another id starts from
    // loading, never from the previous resource's value.
    const [held, setHeld] = useState<{
        readonly id: string;
        readonly load: Load<T>;
    }>({ id, load: Remote.loading });
    const [attempt, setAttempt] = useState(0);
    const version = useRowVersion(id);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` (Retry) and `version` (the row moved) are triggers; the body needs no value from them.
    useEffect(() => {
        let cancelled = false;
        setHeld((prev) => ({
            id,
            load: prev.id === id ? Remote.startLoad(prev.load) : Remote.loading,
        }));
        const run = async () => {
            const r = await read(id);
            if (cancelled) return;
            setHeld((prev) => ({
                id,
                load: Result.isOk(r)
                    ? Remote.loaded(r.value)
                    : prev.id === id && prev.load._tag === "Remote.Reloading"
                      ? Remote.loaded(prev.load.prev)
                      : Remote.failed(r.error),
            }));
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [read, id, attempt, version]);
    const load = held.id === id ? held.load : Remote.loading;
    return { load, retry: () => setAttempt((n) => n + 1) };
};

const SourceView = ({
    result,
    source,
    focusPages,
    onOpenRef,
    onDraftFromSource,
    onReuploadSource,
    onRestored,
}: {
    readonly result: SearchResult;
    readonly source: LoadedSource;
    readonly focusPages: PageSpan | undefined;
    readonly onOpenRef: ResourceDetailProps["onOpenRef"];
    readonly onDraftFromSource: ResourceDetailProps["onDraftFromSource"];
    readonly onReuploadSource: ResourceDetailProps["onReuploadSource"];
    readonly onRestored: () => void;
}): ReactNode => {
    const ref: ResourceRef = {
        type: "source",
        id: result.id,
        title: result.title,
    };
    return (
        <DetailWorkbench
            byline={result.byline}
            title={result.title}
            source={source}
            focusPages={focusPages}
            onOpenMaterial={onOpenRef}
            onDraftFromSource={() => onDraftFromSource(ref)}
            onReupload={
                onReuploadSource === undefined
                    ? undefined
                    : () => onReuploadSource(ref, onRestored)
            }
        />
    );
};

const PackageView = ({
    title,
    pkg,
    downloadExport,
    onOpenRef,
}: {
    readonly title: string;
    readonly pkg: LoadedExport;
    readonly downloadExport: ResourceDetailProps["downloadExport"];
    readonly onOpenRef: ResourceDetailProps["onOpenRef"];
}): ReactNode => {
    const onDownload: ExportViewProps["onDownload"] =
        pkg.downloadUrl !== undefined
            ? () => downloadExport(pkg.downloadUrl ?? "")
            : undefined;
    return (
        <ExportView
            title={title}
            contents={pkg.contents}
            status={pkg.status}
            onDownload={onDownload}
            onOpenRef={onOpenRef}
        />
    );
};

// A package's view in the right panel: its own read, the library's face.
export const PackageDetail = ({
    id,
    title,
    loadExport,
    downloadExport,
    onOpenRef,
}: {
    readonly id: string;
    readonly title: string;
    readonly loadExport: (exportId: string) => PortResult<LoadedExport>;
    readonly downloadExport: ResourceDetailProps["downloadExport"];
    readonly onOpenRef: ResourceDetailProps["onOpenRef"];
}): ReactNode => {
    const { load, retry } = usePortLoad(loadExport, id);
    return (
        <LoadView
            load={load}
            kind="package"
            onRetry={retry}
            render={(pkg) => (
                <PackageView
                    title={title}
                    pkg={pkg}
                    downloadExport={downloadExport}
                    onOpenRef={onOpenRef}
                />
            )}
        />
    );
};

// A material's read-only view (the right panel, stories). `review` (the
// panel's) renders the quality facets under the preview from the SAME
// quality read, which then stays out of the preview's own folded verdict (one
// quality surface, not two).
export const MaterialDetail = ({
    id,
    title,
    loadMaterial,
    evaluateMaterial,
    onOpenRef,
}: {
    readonly id: string;
    readonly title: string;
    readonly loadMaterial: (materialId: string) => PortResult<LoadedMaterial>;
    readonly evaluateMaterial: (
        materialId: string,
    ) => PortResult<MaterialQuality>;
    readonly onOpenRef: ResourceDetailProps["onOpenRef"];
}): ReactNode => {
    const material = usePortLoad(loadMaterial, id);
    const quality = usePortLoad(evaluateMaterial, id);
    const verdict = Remote.isLoaded(quality.load)
        ? quality.load.value
        : undefined;
    return (
        <LoadView
            load={material.load}
            kind="material"
            onRetry={material.retry}
            render={(loaded) => (
                <MaterialPreview
                    title={title}
                    elements={loaded.elements}
                    provenance={loaded.provenance}
                    quality={verdict}
                    onOpenSource={onOpenRef}
                />
            )}
        />
    );
};

// A file's view on its own read: the right panel's (no "Make it a source")
// and the Knowledge bank's (with it).
export const FileDetail = ({
    id,
    title,
    loadFile,
    onPromoteFile,
}: {
    readonly id: string;
    readonly title: string;
    readonly loadFile: (fileId: string) => PortResult<LoadedFile>;
    readonly onPromoteFile?: ResourceDetailProps["onPromoteFile"];
}): ReactNode => {
    const { load, retry } = usePortLoad(loadFile, id);
    return (
        <LoadView
            load={load}
            kind="file"
            onRetry={retry}
            render={(file) => (
                <FileView
                    id={id}
                    title={title}
                    file={file}
                    onPromoteFile={onPromoteFile}
                />
            )}
        />
    );
};

// A file's metadata card.
const FileView = ({
    id,
    title,
    file,
    onPromoteFile,
}: {
    readonly id: string;
    readonly title: string;
    readonly file: LoadedFile;
    readonly onPromoteFile?: ResourceDetailProps["onPromoteFile"];
}): ReactNode => {
    const { t, i18n } = useLingui();
    const factRow = (fact: FileFact): ReactNode => {
        switch (fact.type) {
            case "media-type":
                return (
                    <div key={fact.type}>
                        <dt>{t`Type`}</dt>
                        <dd>{fact.mediaType}</dd>
                    </div>
                );
            case "created":
                return (
                    <div key={fact.type}>
                        <dt>{t`Added`}</dt>
                        <dd>
                            {new Date(fact.iso).toLocaleDateString(
                                i18n.locale,
                                {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                },
                            )}
                        </dd>
                    </div>
                );
            case "description":
                return (
                    <div key={fact.type}>
                        <dt>{t`Description`}</dt>
                        <dd>{fact.text}</dd>
                    </div>
                );
        }
    };
    return (
        <div className="bilda-resource-detail__file">
            <Text as="div" size="sm" tone="dim">
                <Icon name="file" size="sm" /> {t`File`}
            </Text>
            <Heading
                level={2}
                size="xl"
                className="bilda-heading--clamp"
                title={file.title ?? title}
            >
                {file.title ?? title}
            </Heading>
            <dl className="bilda-resource-detail__facts">
                {fileFacts(file).map(factRow)}
            </dl>
            <Text as="p" size="sm" tone="dim">
                <Trans>No preview yet: the file's content isn't stored.</Trans>
            </Text>
            {onPromoteFile !== undefined ? (
                <PromoteFileForm
                    fileId={id}
                    title={file.title ?? title}
                    onPromoteFile={onPromoteFile}
                />
            ) : null}
        </div>
    );
};

// The inline "Make it a source" form. Its open/draft/in-flight state is
// view-ephemeral (lost on reload, no domain meaning); the one mutation is the
// submitted intent, run by the host. On success the host selects the new
// source, so this form unmounts; a failure reopens the draft with the reason.
type PromoteForm =
    | { readonly type: "closed" }
    | {
          readonly type: "editing";
          readonly draft: string;
          readonly error: string | undefined;
      }
    | { readonly type: "submitting"; readonly draft: string };

const PromoteFileForm = ({
    fileId,
    title,
    onPromoteFile,
}: {
    readonly fileId: string;
    readonly title: string;
    readonly onPromoteFile: ResourceDetailProps["onPromoteFile"];
}): ReactNode => {
    const { t } = useLingui();
    const [form, setForm] = useState<PromoteForm>({ type: "closed" });
    const fieldId = `promote-text-${fileId}`;
    if (form.type === "closed")
        return (
            <div className="bilda-resource-detail__file-action">
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                        setForm({
                            type: "editing",
                            draft: "",
                            error: undefined,
                        })
                    }
                >
                    {t`Make it a source`}
                </Button>
            </div>
        );
    const intent = promoteIntentOf(fileId, title, form.draft);
    const submitting = form.type === "submitting";
    const settle = (outcome: PromoteFileOutcome): void => {
        if (outcome.type === "failed")
            setForm({
                type: "editing",
                draft: form.draft,
                error: outcome.message,
            });
    };
    return (
        <form
            className="bilda-resource-detail__file-action"
            onSubmit={(e) => {
                e.preventDefault();
                if (intent === null || submitting) return;
                setForm({ type: "submitting", draft: form.draft });
                onPromoteFile(intent).then(settle);
            }}
        >
            <Field
                label={t`What does the file contain?`}
                htmlFor={fieldId}
                hint={t`The source is built from what you write here; the file itself isn't read.`}
                error={form.type === "editing" ? form.error : undefined}
            >
                <Textarea
                    id={fieldId}
                    value={form.draft}
                    rows={4}
                    disabled={submitting}
                    onChange={(e) =>
                        setForm({
                            type: "editing",
                            draft: e.target.value,
                            error: undefined,
                        })
                    }
                />
            </Field>
            <div className="bilda-resource-detail__file-buttons">
                <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={intent === null || submitting}
                >
                    {submitting ? t`Creating source…` : t`Create source`}
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    disabled={submitting}
                    onClick={() => setForm({ type: "closed" })}
                >
                    {t`Cancel`}
                </Button>
            </div>
        </form>
    );
};

// The in-flight placeholder: the rough shape of a detail (a kind chip, a title,
// a few lines) so the pane doesn't jump when content lands. Decorative; the
// region announces "Loading" once.
const DetailSkeleton = (): ReactNode => {
    const { t } = useLingui();
    return (
        <div
            className="bilda-resource-detail__skeleton"
            role="status"
            aria-label={t`Loading`}
        >
            <Skeleton width="6rem" />
            <Skeleton height="var(--space-8)" width="60%" />
            <Skeleton width="90%" />
            <Skeleton width="80%" />
            <Skeleton width="85%" />
        </div>
    );
};

const UnavailableDetail = ({
    kind,
    onRetry,
}: {
    readonly kind: SearchResultKind;
    readonly onRetry: () => void;
}): ReactNode => {
    const { i18n, t } = useLingui();
    const copy = unavailableCopy(kind);
    return (
        <div className="bilda-resource-detail__centered">
            <EmptyState
                icon={<Icon name={copy.icon} size="lg" />}
                title={i18n._(copy.title)}
                body={i18n._(copy.body)}
            >
                <Button variant="secondary" size="sm" onClick={onRetry}>
                    {t`Try again`}
                </Button>
            </EmptyState>
        </div>
    );
};
